/*
 * Iter XX.76 — Founder-managed Partner Links.
 *
 * Founder tool for minting shareable partner URLs (e.g.
 * askgully.com/john-smith) without waiting for the person to sign up.
 * Reuses the entire existing partner pipeline — clicks land in
 * partner_events, signups in player_attributions, subscription
 * attribution in count_plus_subscribers, and rewards in the
 * partner_earnings_ledger. No new referral logic; no duplicated
 * analytics.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, Edit3, Pause, Play, Link2, Loader2, Plus, MessageSquare, Settings2 } from "lucide-react";

import api from "../lib/api";

const DEBOUNCE_MS = 350;

function copyToClipboard(text, label = "Link") {
  try {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
      return;
    }
  } catch (_) { /* fall through */ }
  // Fallback for older browsers.
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  try { document.execCommand("copy"); toast.success(`${label} copied`); }
  catch (_) { toast.error("Copy failed"); }
  document.body.removeChild(ta);
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium", timeStyle: "short",
    });
  } catch { return iso; }
}

function fmtRewards(amount, currency) {
  if (!amount) return "—";
  const n = Number(amount).toFixed(2);
  return currency ? `${n} ${currency}` : n;
}

// --------------- Create form -------------------------------------------

function CreateLinkForm({ onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [codeEdited, setCodeEdited] = useState(false);
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef(null);

  // Auto-fill code from name unless the founder edits code manually.
  useEffect(() => {
    if (codeEdited) return;
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9\s._-]/g, "")
      .replace(/[\s_]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
    setCode(slug);
  }, [name, codeEdited]);

  // Debounced availability probe.
  const probe = useCallback(async (nameV, codeV) => {
    if (!nameV && !codeV) { setPreview(null); return; }
    try {
      const { data } = await api.post(
        "/admin/partners2/founder-link/preview-code",
        { name: nameV || null, code: codeV || null },
      );
      setPreview(data);
    } catch (_) {
      setPreview({ valid: false, available: false, reason: "network" });
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => probe(name, code), DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [name, code, probe]);

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (!code.trim()) { toast.error("Code is required"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/partners2/founder-link", {
        name: name.trim(),
        email: email.trim() || null,
        code: code.trim(),
        founder_note: note.trim() || null,
      });
      toast.success("Partner link created");
      copyToClipboard(data.root_url, "Link");
      // Reset form.
      setName(""); setEmail(""); setCode(""); setNote("");
      setCodeEdited(false); setPreview(null);
      onCreated?.(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 409) {
        toast.error(
          detail === "username_reserved"
            ? "That handle is reserved. Choose another."
            : "That handle is already taken. Choose another.",
        );
      } else {
        toast.error(detail || "Create failed");
      }
    } finally { setSubmitting(false); }
  }

  const previewValid = preview?.valid && preview?.available;
  const previewReason = preview?.reason;
  const previewUrl = preview?.public_url;

  return (
    <form
      onSubmit={handleSubmit}
      className="surface-1 p-4 space-y-3"
      data-testid="founder-link-create-form"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 block">
            Name<span className="text-red-400"> *</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="John Smith"
            className="w-full bg-neutral-950 border border-neutral-800 rounded-md px-3 py-2 text-sm text-neutral-100 focus:border-emerald-600 outline-none"
            data-testid="founder-link-input-name"
            maxLength={80}
            required
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 block">
            Email <span className="text-neutral-600">(optional — auto-links on signup)</span>
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="john@example.com"
            className="w-full bg-neutral-950 border border-neutral-800 rounded-md px-3 py-2 text-sm text-neutral-100 focus:border-emerald-600 outline-none"
            data-testid="founder-link-input-email"
            maxLength={120}
          />
        </div>
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 block">
          Referral code<span className="text-red-400"> *</span>
          <span className="text-neutral-600 normal-case tracking-normal"> — becomes the URL slug</span>
        </label>
        <div className="flex gap-2 items-stretch">
          <div className="flex-1 flex items-center bg-neutral-950 border border-neutral-800 rounded-md focus-within:border-emerald-600">
            <span className="pl-3 pr-1 text-neutral-500 text-sm select-none">
              askgully.com/
            </span>
            <input
              type="text"
              value={code}
              onChange={(e) => { setCode(e.target.value); setCodeEdited(true); }}
              placeholder="john-smith"
              className="flex-1 bg-transparent border-0 px-1 py-2 text-sm text-neutral-100 focus:outline-none"
              data-testid="founder-link-input-code"
              maxLength={48}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              required
            />
          </div>
          {code && (
            <div
              className={
                "px-2 py-2 rounded-md text-[11px] font-medium min-w-[90px] text-center " +
                (previewValid
                  ? "bg-emerald-950/50 text-emerald-300 border border-emerald-800/60"
                  : preview
                  ? "bg-amber-950/40 text-amber-300 border border-amber-800/60"
                  : "bg-neutral-900 text-neutral-500 border border-neutral-800")
              }
              data-testid="founder-link-preview-status"
            >
              {!preview ? "Checking…"
                : previewValid ? "Available"
                : previewReason === "taken" ? "Taken"
                : previewReason === "username_reserved" ? "Reserved"
                : "Invalid"}
            </div>
          )}
        </div>
        {previewValid && previewUrl && (
          <div
            className="mt-1.5 text-[11px] text-neutral-500 font-mono truncate"
            data-testid="founder-link-preview-url"
          >
            {previewUrl}
          </div>
        )}
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 block">
          Founder note <span className="text-neutral-600">(optional, private)</span>
        </label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Under-19 coach at Bengaluru academy"
          className="w-full bg-neutral-950 border border-neutral-800 rounded-md px-3 py-2 text-sm text-neutral-100 focus:border-emerald-600 outline-none"
          data-testid="founder-link-input-note"
          maxLength={400}
        />
      </div>
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={submitting || !name.trim() || !code.trim() || (preview && !previewValid)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 text-neutral-950 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="founder-link-submit"
        >
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Create link
        </button>
      </div>
    </form>
  );
}

// --------------- Row -----------------------------------------------------

function StatusPill({ status }) {
  const cls = status === "active" ? "bg-emerald-500/10 text-emerald-400 border-emerald-800/60" :
              status === "paused" ? "bg-amber-500/10 text-amber-400 border-amber-800/60" :
              status === "suspended" ? "bg-red-500/10 text-red-400 border-red-800/60" :
              status === "terminated" ? "bg-red-500/10 text-red-400 border-red-800/60" :
              "bg-neutral-800 text-neutral-400 border-neutral-700";
  return (
    <span
      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${cls}`}
      data-testid={`founder-link-status-${status}`}
    >
      {status || "?"}
    </span>
  );
}

function LinkRow({ row, onChange }) {
  const [editingCode, setEditingCode] = useState(false);
  const [newCode, setNewCode] = useState(row.username);
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaName, setMetaName] = useState(row.display_name || "");
  const [metaEmail, setMetaEmail] = useState(row.pending_email || "");
  const [metaNote, setMetaNote] = useState(row.founder_note || "");
  const [busy, setBusy] = useState(false);

  async function saveNewCode() {
    if (!newCode.trim() || newCode === row.username) { setEditingCode(false); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/admin/partners2/founder-link/${row.partner_id}/code`,
        { code: newCode.trim() },
      );
      toast.success("Code updated");
      onChange?.({ ...row, username: data.partner.username, root_url: data.root_url, public_url: data.public_url });
      setEditingCode(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 409) toast.error("That handle is taken");
      else toast.error(detail || "Update failed");
    } finally { setBusy(false); }
  }

  async function saveMeta() {
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/admin/partners2/founder-link/${row.partner_id}`,
        {
          name: metaName || null,
          email: metaEmail || null,
          founder_note: metaNote || null,
        },
      );
      toast.success("Details updated");
      onChange?.({
        ...row,
        display_name: data.partner.display_name,
        pending_email: data.partner.pending_email,
        founder_note: data.partner.founder_note,
      });
      setEditingMeta(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Update failed");
    } finally { setBusy(false); }
  }

  async function setStatus(nextStatus) {
    if (busy) return;
    if (nextStatus === row.status) return;
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/admin/partners2/${row.partner_id}`,
        { status: nextStatus, note: `founder-link admin: ${nextStatus}` },
      );
      toast.success(nextStatus === "active" ? "Reactivated" : "Deactivated");
      onChange?.({ ...row, status: data.partner.status });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Status change failed");
    } finally { setBusy(false); }
  }

  async function copyInviteMessage() {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.get(
        `/admin/partners2/founder-link/${row.partner_id}/invite-message`,
      );
      if (data?.message) {
        copyToClipboard(data.message, "Invite message");
      } else {
        toast.error("Empty template");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't render message");
    } finally { setBusy(false); }
  }

  const isClaimed = !!row.owner_user_id;
  const m = row.metrics || {};

  return (
    <tr
      className="border-b border-neutral-900 hover:bg-neutral-900/40"
      data-testid={`founder-link-row-${row.partner_id}`}
    >
      <td className="px-3 py-2.5 align-top">
        {!editingMeta ? (
          <>
            <div
              className="text-sm text-neutral-100 font-medium"
              data-testid={`founder-link-row-name-${row.partner_id}`}
            >
              {row.display_name || "—"}
            </div>
            <div
              className="text-[11px] text-neutral-500"
              data-testid={`founder-link-row-email-${row.partner_id}`}
            >
              {row.pending_email || <span className="text-neutral-700">no email</span>}
              {isClaimed && (
                <span
                  className="ml-1 text-[10px] uppercase tracking-wider text-emerald-500"
                  data-testid={`founder-link-row-claimed-${row.partner_id}`}
                >
                  · claimed
                </span>
              )}
            </div>
            {row.founder_note && (
              <div className="text-[11px] text-neutral-600 italic mt-0.5 max-w-[220px] truncate">
                {row.founder_note}
              </div>
            )}
          </>
        ) : (
          <div className="space-y-1.5" data-testid={`founder-link-meta-edit-${row.partner_id}`}>
            <input
              value={metaName}
              onChange={(e) => setMetaName(e.target.value)}
              placeholder="Name"
              className="w-full bg-neutral-950 border border-neutral-800 rounded px-2 py-1 text-xs"
            />
            <input
              value={metaEmail}
              onChange={(e) => setMetaEmail(e.target.value)}
              placeholder="Email (optional)"
              className="w-full bg-neutral-950 border border-neutral-800 rounded px-2 py-1 text-xs"
            />
            <input
              value={metaNote}
              onChange={(e) => setMetaNote(e.target.value)}
              placeholder="Note (optional)"
              className="w-full bg-neutral-950 border border-neutral-800 rounded px-2 py-1 text-xs"
            />
            <div className="flex gap-1">
              <button
                onClick={saveMeta}
                disabled={busy}
                className="text-[10px] px-2 py-0.5 rounded bg-emerald-600 hover:bg-emerald-500 text-neutral-950 disabled:opacity-40"
                data-testid={`founder-link-meta-save-${row.partner_id}`}
              >Save</button>
              <button
                onClick={() => {
                  setEditingMeta(false);
                  setMetaName(row.display_name || "");
                  setMetaEmail(row.pending_email || "");
                  setMetaNote(row.founder_note || "");
                }}
                className="text-[10px] px-2 py-0.5 rounded border border-neutral-700 text-neutral-400"
              >Cancel</button>
            </div>
          </div>
        )}
      </td>

      <td className="px-3 py-2.5 align-top">
        {!editingCode ? (
          <div className="flex items-center gap-1">
            <code
              className="text-[12px] text-emerald-300 bg-neutral-900 px-1.5 py-0.5 rounded"
              data-testid={`founder-link-row-code-${row.partner_id}`}
            >
              {row.username}
            </code>
            <button
              onClick={() => { setEditingCode(true); setNewCode(row.username); }}
              className="text-neutral-600 hover:text-neutral-300"
              data-testid={`founder-link-row-edit-code-${row.partner_id}`}
              title="Edit code"
            ><Edit3 size={12} /></button>
          </div>
        ) : (
          <div className="flex items-center gap-1" data-testid={`founder-link-code-edit-${row.partner_id}`}>
            <input
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              className="w-28 bg-neutral-950 border border-neutral-800 rounded px-1.5 py-0.5 text-[12px] text-emerald-300"
              maxLength={48}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") saveNewCode();
                if (e.key === "Escape") { setEditingCode(false); setNewCode(row.username); }
              }}
            />
            <button
              onClick={saveNewCode}
              disabled={busy}
              className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-600 hover:bg-emerald-500 text-neutral-950 disabled:opacity-40"
              data-testid={`founder-link-code-save-${row.partner_id}`}
            >Save</button>
            <button
              onClick={() => { setEditingCode(false); setNewCode(row.username); }}
              className="text-[10px] px-1.5 py-0.5 rounded border border-neutral-700 text-neutral-400"
            >X</button>
          </div>
        )}
      </td>

      <td className="px-3 py-2.5 align-top">
        <div className="flex items-center gap-1.5">
          <a
            href={row.root_url}
            target="_blank"
            rel="noreferrer"
            className="text-[12px] text-neutral-300 hover:text-emerald-300 truncate max-w-[220px] font-mono"
            data-testid={`founder-link-row-url-${row.partner_id}`}
            title={row.root_url}
          >
            {row.root_url?.replace(/^https?:\/\//, "")}
          </a>
          <button
            onClick={() => copyToClipboard(row.root_url, "Link")}
            className="text-neutral-600 hover:text-emerald-300"
            data-testid={`founder-link-row-copy-${row.partner_id}`}
            title="Copy link"
          ><Copy size={12} /></button>
        </div>
      </td>

      <td
        className="px-3 py-2.5 text-right text-sm text-neutral-200 font-mono align-top"
        data-testid={`founder-link-row-clicks-${row.partner_id}`}
      >{m.clicks ?? 0}</td>
      <td
        className="px-3 py-2.5 text-right text-sm text-neutral-200 font-mono align-top"
        data-testid={`founder-link-row-signups-${row.partner_id}`}
      >{m.signups ?? 0}</td>
      <td
        className="px-3 py-2.5 text-right text-sm text-emerald-300 font-mono align-top"
        data-testid={`founder-link-row-paid-${row.partner_id}`}
      >{m.paid_subscribers ?? 0}</td>
      <td
        className="px-3 py-2.5 text-right text-sm text-neutral-200 font-mono align-top"
        data-testid={`founder-link-row-rewards-${row.partner_id}`}
      >{fmtRewards(m.rewards_earned_amount, m.rewards_earned_currency)}</td>

      <td className="px-3 py-2.5 align-top">
        <StatusPill status={row.status} />
      </td>

      <td
        className="px-3 py-2.5 align-top text-[11px] text-neutral-500 whitespace-nowrap"
        data-testid={`founder-link-row-created-${row.partner_id}`}
      >{fmtDate(row.created_at)}</td>

      <td className="px-3 py-2.5 align-top">
        <div className="flex gap-1 flex-wrap justify-end">
          <button
            onClick={copyInviteMessage}
            disabled={busy}
            className="px-2 py-0.5 rounded border border-emerald-900/60 hover:border-emerald-500 text-emerald-400 text-[11px] inline-flex items-center gap-1 disabled:opacity-40"
            data-testid={`founder-link-row-copy-invite-${row.partner_id}`}
            title="Copy invite message"
          ><MessageSquare size={11} /> Copy invite</button>
          <button
            onClick={() => setEditingMeta((v) => !v)}
            className="px-2 py-0.5 rounded border border-neutral-800 hover:border-emerald-600 text-neutral-400 hover:text-emerald-300 text-[11px] inline-flex items-center gap-1"
            data-testid={`founder-link-row-edit-meta-${row.partner_id}`}
          ><Edit3 size={11} /> Edit</button>
          {row.status === "active" ? (
            <button
              onClick={() => setStatus("paused")}
              disabled={busy}
              className="px-2 py-0.5 rounded border border-amber-900/60 hover:border-amber-500 text-amber-400 text-[11px] inline-flex items-center gap-1 disabled:opacity-40"
              data-testid={`founder-link-row-deactivate-${row.partner_id}`}
            ><Pause size={11} /> Deactivate</button>
          ) : (
            <button
              onClick={() => setStatus("active")}
              disabled={busy}
              className="px-2 py-0.5 rounded border border-emerald-900/60 hover:border-emerald-500 text-emerald-400 text-[11px] inline-flex items-center gap-1 disabled:opacity-40"
              data-testid={`founder-link-row-reactivate-${row.partner_id}`}
            ><Play size={11} /> Reactivate</button>
          )}
        </div>
      </td>
    </tr>
  );
}

// --------------- Template editor (default template) ------------------

function TemplateEditor({ onClose }) {
  const [template, setTemplate] = useState(null);
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(
          "/admin/partners2/invite-templates",
        );
        const def = (data?.templates || []).find(
          (t) => t.template_id === "default",
        );
        setTemplate(def);
        setBody(def?.body || "");
      } catch (e) {
        setErr(e?.response?.data?.detail || "load_failed");
      }
    })();
  }, []);

  async function save() {
    if (!body.trim()) { toast.error("Body is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.put(
        "/admin/partners2/invite-templates/default",
        { body },
      );
      toast.success("Template saved");
      setTemplate(data.template);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "save_failed");
    } finally { setSaving(false); }
  }

  return (
    <div
      className="surface-1 p-4 space-y-3"
      data-testid="founder-link-template-editor"
    >
      <div className="text-xs text-neutral-500 leading-relaxed">
        Central template used by <em>Copy invite</em> on every founder-managed
        link. Placeholders substituted at copy-time:
        <code className="text-emerald-300 mx-1">&#123;name&#125;</code>,
        <code className="text-emerald-300 mx-1">&#123;code&#125;</code>,
        <code className="text-emerald-300 mx-1">&#123;root_url&#125;</code>,
        <code className="text-emerald-300 mx-1">&#123;referral_url&#125;</code>,
        <code className="text-emerald-300 mx-1">&#123;display_url&#125;</code>.
        Unknown placeholders are left in place — they won&apos;t crash a copy.
      </div>
      {err && <div className="text-red-400 text-sm">{err}</div>}
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={8}
        className="w-full bg-neutral-950 border border-neutral-800 rounded-md px-3 py-2 text-sm text-neutral-100 focus:border-emerald-600 outline-none font-mono"
        data-testid="founder-link-template-body"
        placeholder="Hey {name} — this is your Gully partner link:&#10;&#10;{root_url}"
      />
      <div className="flex justify-end gap-2">
        <button
          onClick={onClose}
          className="px-3 py-1.5 rounded-md border border-neutral-800 text-neutral-300 text-xs"
          data-testid="founder-link-template-cancel"
        >Cancel</button>
        <button
          onClick={save}
          disabled={saving || !body.trim()}
          className="px-3 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-neutral-950 text-xs font-medium disabled:opacity-40"
          data-testid="founder-link-template-save"
        >{saving ? "Saving…" : "Save template"}</button>
      </div>
      {template?.updated_at && (
        <div className="text-[10px] text-neutral-600">
          last updated {fmtDate(template.updated_at)}
          {template.updated_by ? ` by ${template.updated_by}` : ""}
        </div>
      )}
    </div>
  );
}

// --------------- Section root ------------------------------------------

export function FounderCreatePartnerLinkSection() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [showTemplate, setShowTemplate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/partners2/founder-links", {
        params: { status: statusFilter || undefined },
      });
      setLinks(data.links || []);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "load_failed");
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  function onCreated() {
    load();
  }

  function onRowChange(updated) {
    setLinks((prev) =>
      prev.map((r) => (r.partner_id === updated.partner_id ? { ...r, ...updated } : r)),
    );
  }

  return (
    <section
      data-testid="section-founder-create-partner-link"
      className="space-y-4"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="label-eyebrow flex items-center gap-2">
            <Link2 size={13} className="opacity-70" />
            create partner link · Iter XX.76
          </p>
          <p className="text-xs text-neutral-500 mt-0.5">
            Mint a shareable partner URL for someone who doesn&apos;t have a
            Gully account yet. Clicks, signups, subscriptions, and
            rewards flow through the existing partner pipeline.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowTemplate((v) => !v)}
            className="text-xs px-2.5 py-1 rounded border border-neutral-800 hover:border-emerald-600 text-neutral-300 hover:text-emerald-300 inline-flex items-center gap-1"
            data-testid="founder-link-toggle-template-editor"
          >
            <Settings2 size={12} /> {showTemplate ? "Close template" : "Edit invite template"}
          </button>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-neutral-950 border border-neutral-800 rounded px-2 py-1 text-xs text-neutral-300"
            data-testid="founder-link-status-filter"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="suspended">Suspended</option>
            <option value="terminated">Terminated</option>
          </select>
        </div>
      </div>

      {showTemplate && (
        <TemplateEditor onClose={() => setShowTemplate(false)} />
      )}

      <CreateLinkForm onCreated={onCreated} />

      {err && <div className="surface-1 p-4 text-red-400 text-sm">{err}</div>}

      <div className="surface-1 overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid="founder-link-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-neutral-500 border-b border-neutral-900">
              <th className="px-3 py-2">Name / Email</th>
              <th className="px-3 py-2">Code</th>
              <th className="px-3 py-2">Link</th>
              <th className="px-3 py-2 text-right">Clicks</th>
              <th className="px-3 py-2 text-right">Signups</th>
              <th className="px-3 py-2 text-right">Paid</th>
              <th className="px-3 py-2 text-right">Rewards</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-neutral-500 text-sm">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && links.length === 0 && (
              <tr data-testid="founder-link-empty">
                <td colSpan={10} className="px-3 py-6 text-center text-neutral-500 text-sm">
                  No partner links yet. Create your first one above.
                </td>
              </tr>
            )}
            {!loading && links.map((row) => (
              <LinkRow key={row.partner_id} row={row} onChange={onRowChange} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
